import { cineamoApiClient } from '../../apiClient';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import { CinemaProductDto, CinemaProductInputDto } from '../../models/cinema-product/CinemaProductDto.types';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import { CinemaProductListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

// <------------------------ Cinema Products ------------------------>

type GetCinemaProductsParams = UrlQueryParameter & {
    cinemaId?: number | string;
    cinemaProductCategoryId?: number | string;
    /** Also ask the ticket system for its products (operators only; costs one request to it). */
    withTicketSystemCheck?: boolean;
};

/**
 * This endpoint returns a list of cinema products.
 *
 * @example
 * // Get products offerd by a specific cinema:
 * getCinemaProductsByCinemaId({cinemaId: 1});
 *
 * @param urlParams
 * @returns {ApiResponse<CinemaProductListResponse>}
 */

export async function getCinemaProductsByCinemaId(
    urlParams?: GetCinemaProductsParams
): Promise<ApiResponse<CinemaProductListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<CinemaProductListResponse>>(`/cinema-products${buildUrlQuery(urlParams)}`)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Cinema Product ------------------------>

/**
 * This endpoint returns a cinema product.
 *
 * @example
 * // Get single product by id:
 * getCinemaProductById(1);
 *
 * @param id
 * @returns {ApiResponse<CinemaProductDto>}
 */

export async function getCinemaProductById(
    id: number | string,
    urlParams?: Pick<GetCinemaProductsParams, 'withTicketSystemCheck'>
): Promise<ApiResponse<CinemaProductDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<CinemaProductDto>>(`/cinema-products/${id}${buildUrlQuery(urlParams)}`)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Create Cinema Product ------------------------>

/**
 * This endpoint returns a cinema product.
 *
 * @example
 * // Create a single product to a cinema:
 * getCinemaProductById({...cinemaProduct});
 *
 * @param cinemaProduct
 * @returns {ApiResponse<CinemaProductDto> | ApiErrorResponse<CinemaProductDto>}
 */

export async function createCinemaProduct(
    cinemaProduct: CinemaProductInputDto
): Promise<ApiResponse<CinemaProductDto> | ApiErrorResponse<CinemaProductInputDto>> {
    return cineamoApiClient
        .post('/cinema-products', cinemaProduct)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Update Cinema Product ------------------------>

/**
 * This endpoint returns a cinema product.
 *
 * @example
 * // Update a single cinema product:
 * updateCinemaProductById(1, {...cinemaProduct});
 *
 * @param cinemaProduct
 * @param cinemaProductId
 * @returns {ApiResponse<CinemaProductDto> | ApiErrorResponse<CinemaProductDto>}
 */

export async function updateCinemaProductById(
    cinemaProductId: number | string,
    cinemaProduct: CinemaProductDto
): Promise<ApiResponse<CinemaProductDto> | ApiErrorResponse<CinemaProductDto>> {
    return cineamoApiClient
        .patch(`/cinema-products/${cinemaProductId}`, cinemaProduct)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Set Cinema Product Category ------------------------>

/**
 * This endpoint returns a cinema product.
 *
 * @example
 * // Update a single cinema product categories:
 * setCinemaProductCategory(1, 1);
 *
 * @param cinemaProductId
 * @param categoryId
 * @returns {ApiResponse<CinemaProductDto> | ApiErrorResponse<CinemaProductDto>}
 */

type SetCinemaProductCategoriesParams = UrlQueryParameter & {
    categories: string[];
};

export async function setCinemaProductCategories(
    cinemaProductId: number | string,
    categories: SetCinemaProductCategoriesParams
): Promise<ApiResponse<CinemaProductDto> | ApiErrorResponse<CinemaProductDto>> {
    return cineamoApiClient
        .put(`/cinema-products/${cinemaProductId}/categories`, categories)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Delete Cinema Product ------------------------>

/**
 * This endpoint returns a boolean.
 *
 * @example
 * // Delete a single cinema product by id:
 * deleteCinemaProductById(1);
 *
 * @param cinemaProduct
 * @returns {boolean}
 */

export async function deleteCinemaProductById(cinemaProduct: number | string): Promise<boolean> {
    return cineamoApiClient
        .delete(`/cinema-products/${cinemaProduct}`)
        .then((response) => response.status === 204)
        .catch(() => {
            return false;
        });
}
